/**
 * FrontDesk+ patient-info RPC client.
 *
 * A standalone client for the contract-owned `FrontDeskPatientInfoRpcs` group, over the same bounded,
 * SOL-authenticated HTTPS transport as the main bridge client (reused via `createDocCirrusRpcLayer`,
 * so this file adds no new transport and does not touch the shared client module).
 *
 * The three patient-review operations map the server's single non-enumerating rejection to a thrown
 * error; the XState caller treats any failure as "skip the optional review". The two policy operations
 * return typed settings outcomes. Raw patient values from the review response live only in caller
 * memory — never logged here. Hausarzt free text follows the same gated transport and is task-only on
 * the server.
 *
 * @since 1.0.0
 */

import { Effect } from "effect";
import { RpcClient } from "effect/rpc";
import { FrontDeskPatientInfoRpcs } from "@ascleo/frontdesk-contracts/patient-info/rpc";
import type { EgkAttestationHandle } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
import type { PatientInfoReview } from "@ascleo/frontdesk-contracts/patient-info/review";
import type {
  PatientInfoSubmitRequest,
  PatientInfoSubmitResult,
} from "@ascleo/frontdesk-contracts/patient-info/submit";
import type {
  PatientInfoPolicySaveResponse,
  PatientInfoPolicyUpdate,
  PatientInfoPolicyView,
} from "@ascleo/frontdesk-contracts/patient-info/policy";
import { createDocCirrusRpcLayer, getBridgeUrl } from "./doc-cirrus.client";

/**
 * Client for the FrontDesk+ patient-info review.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { patientInfoRpcClient } from "./patient-info.client";
 *
 * const review = await patientInfoRpcClient.review("opaque-handle");
 * ```
 */
export const patientInfoRpcClient = {
  /** Practice-wide review settings available to the PIN-protected settings screen. */
  policy: {
    /** Read the current server-owned master and field switches. */
    read: (): Promise<PatientInfoPolicyView> =>
      Effect.runPromise(
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(FrontDeskPatientInfoRpcs);
            return yield* client.FrontDeskPatientInfoPolicyRead({});
          }),
        ).pipe(Effect.provide(createDocCirrusRpcLayer(getBridgeUrl()))),
      ),

    /** Save the switches using the version observed by the settings screen. */
    save: (
      request: PatientInfoPolicyUpdate,
    ): Promise<PatientInfoPolicySaveResponse> =>
      Effect.runPromise(
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(FrontDeskPatientInfoRpcs);
            return yield* client.FrontDeskPatientInfoPolicySave(request);
          }),
        ).pipe(Effect.provide(createDocCirrusRpcLayer(getBridgeUrl()))),
      ),
  },

  /**
   * Open (or re-read) the review for the given opaque eGK attestation handle.
   *
   * @since 1.0.0
   */
  review: (egkAttestation: EgkAttestationHandle): Promise<PatientInfoReview> =>
    Effect.runPromise(
      Effect.scoped(
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FrontDeskPatientInfoRpcs);
          return yield* client.FrontDeskPatientInfoReview({ egkAttestation });
        }),
      ).pipe(Effect.provide(createDocCirrusRpcLayer(getBridgeUrl()))),
    ),

  /**
   * Submit the one proposal. Reuses the caller's `submissionId` on retry so the server recognises the
   * same save.
   *
   * @since 1.0.0
   */
  submit: (
    request: PatientInfoSubmitRequest,
  ): Promise<PatientInfoSubmitResult> =>
    Effect.runPromise(
      Effect.scoped(
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FrontDeskPatientInfoRpcs);
          return yield* client.FrontDeskPatientInfoSubmit(request);
        }),
      ).pipe(Effect.provide(createDocCirrusRpcLayer(getBridgeUrl()))),
    ),

  /**
   * Best-effort revoke on skip/timeout/reset/exit. The caller clears its own state regardless of the
   * result.
   *
   * @since 1.0.0
   */
  revoke: (
    egkAttestation: EgkAttestationHandle,
  ): Promise<{ readonly revoked: true }> =>
    Effect.runPromise(
      Effect.scoped(
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FrontDeskPatientInfoRpcs);
          return yield* client.FrontDeskPatientInfoRevoke({ egkAttestation });
        }),
      ).pipe(Effect.provide(createDocCirrusRpcLayer(getBridgeUrl()))),
    ),
};

/**
 * Fire-and-forget best-effort revoke for skip / reset / unmount.
 *
 * Swallows every error: the client clears its own state regardless, and the server's hard expiry plus
 * TTL are the backstop. Never blocks the UI or surfaces a failure.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { revokePatientInfoQuietly } from "./patient-info.client";
 *
 * revokePatientInfoQuietly(handle); // no await needed
 * ```
 */
export function revokePatientInfoQuietly(
  egkAttestation: EgkAttestationHandle,
): void {
  void patientInfoRpcClient.revoke(egkAttestation).catch(() => {
    // Intentionally ignored — best-effort; TTL/expiry are the hard backstop.
  });
}
